import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import useSWR, { useSWRConfig } from "swr";
import {
  Stack,
  Text,
  Button,
  Center,
  Container,
  Flex,
  Select,
  Box,
  Badge,
} from "@chakra-ui/react";
import { QuestionnaireHeader } from "../../components/Form";
import { AttentionModal, SuccessModal } from "../../components/Modals";
import baseService from "../../services/base";
import Loader from "../../components/Feedback/Loader";
import { QuestionCardRespondents } from "../../components/Card";
import { fetchData } from "../../services/fetch";

async function fetchSurvey(url) {
  const response = await baseService.get(url);
  return response?.data?.[0];
}

export default function Reviewers() {
  const navigate = useNavigate();
  const { id: surveyId } = useParams();
  const params = new URLSearchParams(window.location.search);
  const companyId = params?.get("cr");
  const companyGroupId = params?.get("cg");

  const [reviewersList, setReviewersList] = useState();
  const { mutate } = useSWRConfig();

  const { data: surveyData } = useSWR(
    companyId
      ? `/link_user_question/survey/${surveyId}?CompanyGroupId=${companyGroupId}&id_company=${companyId}`
      : null,
    fetchSurvey
  );

  const { data: companyUsers } = useSWR(
    companyGroupId ? `user?group=${companyGroupId}` : null,
    fetchData
  );

  // Buscar revisores já atribuídos
  const { data: existingReviewers } = useSWR(
    surveyId ? `/question-reviewer/survey/${surveyId}` : null,
    fetchData
  );

  const {
    handleSubmit,
    setValue,
    control,
    formState: { isDirty, isSubmitting },
  } = useForm();

  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);

  useEffect(() => {
    if (companyUsers) {
      const reviewers =
        companyUsers?.filter((user) => {
          // Filtra usuários com role RESPONSAVEL
          const userRoles = user?.roles || [];
          const isResponsavel = userRoles.some((role) => role?.name === "RESPONSAVEL");
          return isResponsavel;
        }) || [];
      setReviewersList(reviewers);
    }
  }, [companyUsers]);

  // Inicializar revisores
  useEffect(() => {
    if (existingReviewers && surveyData?.survey?.questions) {
      surveyData.survey.questions.forEach((question) => {
        const fieldName = `reviewer_${question.id_question_template}`;
        const existingReviewer = existingReviewers.find(
          (rev) => rev.id_question_template === question.id_question_template
        );
        const userId = existingReviewer?.id_user || "";
        setValue(fieldName, userId, { shouldDirty: false });
      });
    }
  }, [existingReviewers, surveyData, setValue]);

  async function onSubmit(formData) {
    try {
      const reviewerQuestions = [];
      
      if (surveyData?.survey?.questions) {
        surveyData.survey.questions.forEach((question) => {
          const fieldName = `reviewer_${question.id_question_template}`;
          const userId = formData[fieldName];
          
          reviewerQuestions.push({
            id_question_template: question.id_question_template,
            id_user: userId && userId !== "" ? parseInt(userId) : null,
          });
        });
      }

      if (reviewerQuestions.length > 0) {
        const payload = {
          id_survey: +surveyId,
          questions: reviewerQuestions,
        };
        
        await baseService.post("/question-reviewer/bulk-update", payload);
      }

      setSuccessModalIsOpen(true);
      mutate(`/question-reviewer/survey/${surveyId}`);
    } catch (err) {
      console.error("❌ Erro ao salvar:", err);
      console.error("Detalhes:", err.response?.data);
      alert(`Erro ao salvar: ${err.response?.data?.message || err.message}`);
    }
  }

  function closeSuccessModal() {
    setSuccessModalIsOpen(false);
    navigate(-1);
  }

  function openConfirmationModal() {
    setConfirmationModalIsOpen(true);
  }

  function closeConfirmationModal() {
    setConfirmationModalIsOpen(false);
  }

  function confirmAndGoBack() {
    setConfirmationModalIsOpen(false);
    navigate(-1);
  }

  if (!surveyData || !reviewersList) {
    return <Loader />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <QuestionnaireHeader
        tagLabel={surveyData?.survey?.description}
        heading="Gestão de Revisores"
        description="Atribua revisores às questões do questionário"
      >
        <Stack spacing="3" display={["none", "flex"]}>
          <Button
            type="submit"
            variant="primary"
            w="271px"
            height="40px"
            isDisabled={!isDirty}
            isLoading={isSubmitting}
          >
            Salvar Revisores
          </Button>
          <Button variant="cancel" onClick={openConfirmationModal}>
            Voltar
          </Button>
        </Stack>
      </QuestionnaireHeader>

      {!surveyData?.survey?.questions && (
        <Flex mt="32px" justify="center" w="full">
          <Text fontSize="24px" fontWeight="500">
            Não existem perguntas para esse questionário.
          </Text>
        </Flex>
      )}

      {surveyData?.survey?.questions && (
        <Center px={{ xl: "86px", "2xl": "unset" }}>
          <Container
            maxW={"1280px"}
            p={["0 24px", "0 0 32px"]}
            overflowX="hidden"
          >
            <Box
              bg="#E6F1F8"
              p="16px"
              borderRadius="8px"
              mb="24px"
              mt="24px"
            >
              <Text fontSize="14px" fontWeight="500" color="#333" mb="8px">
                ℹ️ Como funciona a atribuição de revisores:
              </Text>
              <Text fontSize="13px" color="#555" mb="4px">
                • <strong>Revisores:</strong> Selecione o usuário responsável por revisar cada questão
              </Text>
              <Text fontSize="13px" color="#555" mb="4px">
                • Apenas usuários com perfil{" "}
                <Badge colorScheme="purple" fontSize="11px">
                  RESPONSAVEL
                </Badge>{" "}
                podem ser atribuídos
              </Text>
              <Text fontSize="13px" color="#555">
                • Cada questão pode ter apenas <strong>um revisor</strong>
              </Text>
            </Box>

            <Stack spacing="24px">
              {surveyData?.survey?.questions?.map((question, index) => (
                <Box
                  key={question.id_question_template}
                  bg="white"
                  borderRadius="8px"
                  border="1px solid #E2E8F0"
                  p="20px"
                >
                  <QuestionCardRespondents
                    key={index}
                    index={index}
                    questionId={question?.id_question}
                    questionType={question?.code || ""}
                    question={question?.description}
                    idPillar={question?.id_pillar}
                    idTheme={question?.id_theme}
                    pillarName={question?.pillar_name}
                    themeTitle={question?.theme_title}
                    gri={question?.gri}
                  >
                    <Box mt="20px">
                      <Stack spacing="20px" w="full">
                        {/* Revisor */}
                        <Box>
                          <Flex align="center" gap="8px" mb="12px">
                            <Text fontWeight={600} fontSize="14px" color="#333">
                              🔍 Revisor da Questão
                            </Text>
                            <Badge colorScheme="purple" fontSize="11px">
                              Opcional
                            </Badge>
                          </Flex>
                          <Controller
                            name={`reviewer_${question.id_question_template}`}
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <Select
                                {...field}
                                placeholder="Selecione um revisor (opcional)"
                                bg="white"
                                borderColor="#CBD5E0"
                                _hover={{ borderColor: "#A0AEC0" }}
                                _focus={{
                                  borderColor: "#805AD5",
                                  boxShadow: "0 0 0 1px #805AD5",
                                }}
                              >
                                {reviewersList?.map((user) => (
                                  <option key={user.id_user} value={user.id_user}>
                                    {user.username} - {user.email}
                                  </option>
                                ))}
                              </Select>
                            )}
                          />
                        </Box>
                      </Stack>
                    </Box>
                  </QuestionCardRespondents>
                </Box>
              ))}
            </Stack>
          </Container>
        </Center>
      )}

      <AttentionModal
        isOpen={confirmationModalIsOpen}
        onClose={closeConfirmationModal}
        submit={confirmAndGoBack}
        title="Confirme as configurações"
        content="Verifique se os revisores foram atribuídos corretamente."
        buttonText="Corrigir"
      />

      <SuccessModal
        isOpen={successModalIsOpen}
        onClose={closeSuccessModal}
        title="Revisores salvos com sucesso!"
        content="Os revisores foram atribuídos às questões."
      />
    </form>
  );
}
